import { CheckCircle2, XCircle, Car, Navigation, UserCheck, MapPin, Flag } from "lucide-react";

export const STATUS_META = {
  confirmed: { label: "Confirmed", color: "#34D399", Icon: CheckCircle2, live: false },
  driver_assigned: { label: "Chauffeur Assigned", color: "#FBBF24", Icon: UserCheck, live: false },
  driver_en_route: { label: "En Route", color: "#60A5FA", Icon: Navigation, live: true },
  driver_arrived: { label: "Arrived at Pickup", color: "#818CF8", Icon: MapPin, live: true },
  passenger_on_board: { label: "Passenger On Board", color: "#22D3EE", Icon: Car, live: true },
  in_progress: { label: "In Transit", color: "#D4AF37", Icon: Car, live: true },
  completed: { label: "Completed", color: "#A1A1AA", Icon: Flag, live: false },
  canceled: { label: "Canceled", color: "#FB7185", Icon: XCircle, live: false },
};

export function StatusBadge({ status, size = "sm" }) {
  const m = STATUS_META[status] || STATUS_META.confirmed;
  const { Icon } = m;
  const pad = size === "lg" ? "px-3.5 py-1.5 text-sm" : "px-2.5 py-1 text-xs";
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-2 rounded-full border font-medium ${pad}`}
      style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}14` }}
    >
      <span className="relative flex h-2 w-2">
        {m.live && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: m.color }} />}
        <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: m.color }} />
      </span>
      <Icon className="h-3.5 w-3.5" /> {m.label}
    </span>
  );
}

export function StatCard({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-gold/15 bg-obsidian-800 p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ivory/50">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold" style={{ color: accent || "#F8F9FA" }}>{value}</p>
    </div>
  );
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function tzAbbr() {
  try {
    return new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || "";
  } catch { return ""; }
}

const HEALTH = {
  connected: { label: "Connected", color: "#10B981" },
  delayed: { label: "Delayed", color: "#F59E0B" },
  disconnected: { label: "Disconnected", color: "#EF4444" },
};

export function SyncHealthBanner({ health }) {
  if (!health) return null;
  const h = HEALTH[health.status] || HEALTH.disconnected;
  const stale = health.status !== "connected";
  return (
    <div data-testid="sync-health-banner" className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-2.5 text-xs"
      style={{ borderColor: `${h.color}44`, background: `${h.color}0f` }}>
      <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: h.color }} data-testid={`sync-status-${health.status}`}>
        <span className="h-2 w-2 rounded-full" style={{ background: h.color }} /> {h.label}
      </span>
      <span className="text-ivory/55">Last sync {health.last_success_at ? fmtDateTime(health.last_success_at) : "never"}</span>
      {health.last_verified_update && <span className="text-ivory/45">· Latest update {fmtDateTime(health.last_verified_update)}</span>}
      {stale && <span className="font-medium text-amber-400">· Showing last verified data — statuses may be outdated (not live)</span>}
    </div>
  );
}
